/**
 * Curated interests taxonomy — the single source both the profile picker UI
 * and the matching engine consume. Users store `Interest.id` strings in
 * `UserProfile.interests` (see types/user.ts); labels and emoji live only
 * here so copy can evolve without migrating stored ids.
 *
 * Rules for editing:
 * - `Interest.id` is a STABLE kebab-case key. Never rename or reuse one —
 *   stored profiles reference it forever. Retire by removing; add new ids.
 * - Labels are display copy, ≤ 22 characters, India-first vocabulary.
 * - Every `Interest.category` must be an `InterestCategory.id`.
 */

export interface InterestCategory {
  /** Stable kebab-case key. */
  id: string;
  emoji: string;
  label: string;
}

export interface Interest {
  /** Stable kebab-case key — this is what gets stored on profiles. */
  id: string;
  /** Display copy, ≤ 22 characters. */
  label: string;
  /** An `InterestCategory.id`. */
  category: string;
}

export const INTEREST_CATEGORIES: InterestCategory[] = [
  { id: 'food-chai', emoji: '🍵', label: 'Food & Chai' },
  { id: 'music-arts', emoji: '🎶', label: 'Music & Arts' },
  { id: 'screens-stories', emoji: '🎬', label: 'Screens & Stories' },
  { id: 'sport-movement', emoji: '🏏', label: 'Sport & Movement' },
  { id: 'mind-belief', emoji: '🕉', label: 'Mind & Belief' },
  { id: 'making-building', emoji: '🛠', label: 'Making & Building' },
  { id: 'outdoors', emoji: '🌄', label: 'Outdoors' },
  { id: 'people-causes', emoji: '🤝', label: 'People & Causes' },
  { id: 'games-play', emoji: '🎲', label: 'Games & Play' },
  { id: 'style', emoji: '🧿', label: 'Style' },
];

export const INTERESTS: Interest[] = [
  // ── Food & Chai 🍵 ──────────────────────────────────────────────
  { id: 'chai', label: 'Chai', category: 'food-chai' },
  { id: 'filter-coffee', label: 'Filter coffee', category: 'food-chai' },
  { id: 'street-food', label: 'Street food', category: 'food-chai' },
  { id: 'biryani', label: 'Biryani', category: 'food-chai' },
  { id: 'south-indian-food', label: 'South Indian food', category: 'food-chai' },
  { id: 'regional-thalis', label: 'Regional thalis', category: 'food-chai' },
  { id: 'mithai', label: 'Mithai & desserts', category: 'food-chai' },
  { id: 'home-cooking', label: 'Home cooking', category: 'food-chai' },
  { id: 'baking', label: 'Baking', category: 'food-chai' },
  { id: 'achar-pickles', label: 'Achar & pickles', category: 'food-chai' },
  { id: 'cafe-hopping', label: 'Cafe hopping', category: 'food-chai' },
  { id: 'food-vlogging', label: 'Food vlogging', category: 'food-chai' },
  { id: 'vegan-cooking', label: 'Vegan cooking', category: 'food-chai' },

  // ── Music & Arts 🎶 ─────────────────────────────────────────────
  { id: 'indie-music', label: 'Indie music', category: 'music-arts' },
  { id: 'ghazals', label: 'Ghazals', category: 'music-arts' },
  { id: 'hindustani-classical', label: 'Hindustani classical', category: 'music-arts' },
  { id: 'carnatic-music', label: 'Carnatic music', category: 'music-arts' },
  { id: 'bollywood-music', label: 'Bollywood music', category: 'music-arts' },
  { id: 'desi-hip-hop', label: 'Desi hip-hop', category: 'music-arts' },
  { id: 'playing-guitar', label: 'Playing guitar', category: 'music-arts' },
  { id: 'singing', label: 'Singing', category: 'music-arts' },
  { id: 'classical-dance', label: 'Classical dance', category: 'music-arts' },
  { id: 'street-dance', label: 'Street dance', category: 'music-arts' },
  { id: 'painting', label: 'Painting', category: 'music-arts' },
  { id: 'sketching', label: 'Sketching', category: 'music-arts' },
  { id: 'rangoli', label: 'Rangoli', category: 'music-arts' },
  { id: 'live-gigs', label: 'Live gigs', category: 'music-arts' },

  // ── Screens & Stories 🎬 ────────────────────────────────────────
  { id: 'bollywood', label: 'Bollywood', category: 'screens-stories' },
  { id: 'regional-cinema', label: 'Regional cinema', category: 'screens-stories' },
  { id: 'world-cinema', label: 'World cinema', category: 'screens-stories' },
  { id: 'anime', label: 'Anime', category: 'screens-stories' },
  { id: 'k-dramas', label: 'K-dramas', category: 'screens-stories' },
  { id: 'web-series', label: 'Web series', category: 'screens-stories' },
  { id: 'documentaries', label: 'Documentaries', category: 'screens-stories' },
  { id: 'standup-comedy', label: 'Standup comedy', category: 'screens-stories' },
  { id: 'reading-fiction', label: 'Reading fiction', category: 'screens-stories' },
  { id: 'nonfiction-books', label: 'Non-fiction books', category: 'screens-stories' },
  { id: 'poetry-shayari', label: 'Poetry & shayari', category: 'screens-stories' },
  { id: 'mythology-epics', label: 'Mythology & epics', category: 'screens-stories' },
  { id: 'fan-fiction', label: 'Fan fiction', category: 'screens-stories' },
  { id: 'podcasts', label: 'Podcasts', category: 'screens-stories' },

  // ── Sport & Movement 🏏 ─────────────────────────────────────────
  { id: 'cricket', label: 'Cricket', category: 'sport-movement' },
  { id: 'football', label: 'Football', category: 'sport-movement' },
  { id: 'kabaddi', label: 'Kabaddi', category: 'sport-movement' },
  { id: 'badminton', label: 'Badminton', category: 'sport-movement' },
  { id: 'table-tennis', label: 'Table tennis', category: 'sport-movement' },
  { id: 'volleyball', label: 'Volleyball', category: 'sport-movement' },
  { id: 'gymming', label: 'Gymming', category: 'sport-movement' },
  { id: 'yoga', label: 'Yoga', category: 'sport-movement' },
  { id: 'running', label: 'Running', category: 'sport-movement' },
  { id: 'cycling', label: 'Cycling', category: 'sport-movement' },
  { id: 'swimming', label: 'Swimming', category: 'sport-movement' },
  { id: 'martial-arts', label: 'Martial arts', category: 'sport-movement' },
  { id: 'skating', label: 'Skating', category: 'sport-movement' },

  // ── Mind & Belief 🕉 ────────────────────────────────────────────
  { id: 'astrology', label: 'Astrology', category: 'mind-belief' },
  { id: 'tarot', label: 'Tarot', category: 'mind-belief' },
  { id: 'numerology', label: 'Numerology', category: 'mind-belief' },
  { id: 'meditation', label: 'Meditation', category: 'mind-belief' },
  { id: 'spirituality', label: 'Spirituality', category: 'mind-belief' },
  { id: 'vedic-chanting', label: 'Vedic chanting', category: 'mind-belief' },
  { id: 'temple-hopping', label: 'Temple hopping', category: 'mind-belief' },
  { id: 'temple-architecture', label: 'Temple architecture', category: 'mind-belief' },
  { id: 'philosophy', label: 'Philosophy', category: 'mind-belief' },
  { id: 'psychology', label: 'Psychology', category: 'mind-belief' },
  { id: 'journaling', label: 'Journaling', category: 'mind-belief' },
  { id: 'self-improvement', label: 'Self-improvement', category: 'mind-belief' },

  // ── Making & Building 🛠 ────────────────────────────────────────
  { id: 'startups', label: 'Startups', category: 'making-building' },
  { id: 'side-hustles', label: 'Side hustles', category: 'making-building' },
  { id: 'coding', label: 'Coding', category: 'making-building' },
  { id: 'ai-tinkering', label: 'AI tinkering', category: 'making-building' },
  { id: 'product-design', label: 'Product design', category: 'making-building' },
  { id: 'photography', label: 'Photography', category: 'making-building' },
  { id: 'filmmaking', label: 'Filmmaking', category: 'making-building' },
  { id: 'content-creation', label: 'Content creation', category: 'making-building' },
  { id: 'writing', label: 'Writing', category: 'making-building' },
  { id: 'pottery', label: 'Pottery', category: 'making-building' },
  { id: 'woodworking', label: 'Woodworking', category: 'making-building' },
  { id: 'diy-crafts', label: 'DIY & crafts', category: 'making-building' },
  { id: 'personal-finance', label: 'Personal finance', category: 'making-building' },

  // ── Outdoors 🌄 ─────────────────────────────────────────────────
  { id: 'trekking', label: 'Trekking', category: 'outdoors' },
  { id: 'camping', label: 'Camping', category: 'outdoors' },
  { id: 'backpacking', label: 'Backpacking', category: 'outdoors' },
  { id: 'solo-travel', label: 'Solo travel', category: 'outdoors' },
  { id: 'road-trips', label: 'Road trips', category: 'outdoors' },
  { id: 'bike-trips', label: 'Bike trips', category: 'outdoors' },
  { id: 'monsoon-walks', label: 'Monsoon walks', category: 'outdoors' },
  { id: 'beaches', label: 'Beaches', category: 'outdoors' },
  { id: 'wildlife-safaris', label: 'Wildlife safaris', category: 'outdoors' },
  { id: 'birdwatching', label: 'Birdwatching', category: 'outdoors' },
  { id: 'stargazing', label: 'Stargazing', category: 'outdoors' },
  { id: 'gardening', label: 'Gardening', category: 'outdoors' },

  // ── People & Causes 🤝 ──────────────────────────────────────────
  { id: 'volunteering', label: 'Volunteering', category: 'people-causes' },
  { id: 'animal-rescue', label: 'Animal rescue', category: 'people-causes' },
  { id: 'pets', label: 'Pets', category: 'people-causes' },
  { id: 'climate-action', label: 'Climate action', category: 'people-causes' },
  { id: 'sustainable-living', label: 'Sustainable living', category: 'people-causes' },
  { id: 'mental-health', label: 'Mental health', category: 'people-causes' },
  { id: 'teaching-mentoring', label: 'Teaching & mentoring', category: 'people-causes' },
  { id: 'community-events', label: 'Community events', category: 'people-causes' },
  { id: 'heritage-walks', label: 'Heritage walks', category: 'people-causes' },
  { id: 'gender-equality', label: 'Gender equality', category: 'people-causes' },
  { id: 'debating', label: 'Debating', category: 'people-causes' },

  // ── Games & Play 🎲 ─────────────────────────────────────────────
  { id: 'board-games', label: 'Board games', category: 'games-play' },
  { id: 'chess', label: 'Chess', category: 'games-play' },
  { id: 'carrom', label: 'Carrom', category: 'games-play' },
  { id: 'card-games', label: 'Card games', category: 'games-play' },
  { id: 'video-games', label: 'Video games', category: 'games-play' },
  { id: 'mobile-gaming', label: 'Mobile gaming', category: 'games-play' },
  { id: 'esports', label: 'Esports', category: 'games-play' },
  { id: 'quizzing', label: 'Quizzing', category: 'games-play' },
  { id: 'puzzles-sudoku', label: 'Puzzles & sudoku', category: 'games-play' },
  { id: 'antakshari', label: 'Antakshari', category: 'games-play' },
  { id: 'dumb-charades', label: 'Dumb charades', category: 'games-play' },
  { id: 'fantasy-leagues', label: 'Fantasy leagues', category: 'games-play' },

  // ── Style 🧿 ────────────────────────────────────────────────────
  { id: 'thrifting', label: 'Thrifting', category: 'style' },
  { id: 'streetwear', label: 'Streetwear', category: 'style' },
  { id: 'ethnic-fashion', label: 'Ethnic fashion', category: 'style' },
  { id: 'sarees', label: 'Sarees', category: 'style' },
  { id: 'sneakers', label: 'Sneakers', category: 'style' },
  { id: 'jewellery', label: 'Jewellery', category: 'style' },
  { id: 'mehendi', label: 'Mehendi', category: 'style' },
  { id: 'makeup', label: 'Makeup', category: 'style' },
  { id: 'skincare', label: 'Skincare', category: 'style' },
  { id: 'tattoos', label: 'Tattoos', category: 'style' },
  { id: 'home-decor', label: 'Home decor', category: 'style' },
];

const BY_ID: ReadonlyMap<string, Interest> = new Map(INTERESTS.map((i) => [i.id, i]));

/** Look up one interest by its stable id. Unknown ids return undefined. */
export function interestById(id: string): Interest | undefined {
  return BY_ID.get(id);
}

/**
 * Case-insensitive substring search over labels, for the picker's search box.
 * Blank queries return nothing (the picker shows categories instead).
 */
export function searchInterests(query: string, limit = 20): Interest[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return INTERESTS.filter((i) => i.label.toLowerCase().includes(q)).slice(0, limit);
}

/**
 * Overlap between two profiles' interest-id lists.
 *
 * Pure set math over the ids as given (deduplicated): ids that are no longer
 * in the taxonomy still count, so two profiles holding the same retired id
 * are not artificially pushed apart. `shared` preserves `a`'s order.
 * Jaccard = |A ∩ B| / |A ∪ B|; 0 when both lists are empty.
 */
export function interestOverlap(
  a: string[],
  b: string[],
): { shared: string[]; jaccard: number } {
  const setA = new Set(a);
  const setB = new Set(b);
  const shared = [...setA].filter((id) => setB.has(id));
  const unionSize = new Set([...setA, ...setB]).size;
  return { shared, jaccard: unionSize === 0 ? 0 : shared.length / unionSize };
}
